import { ExternalLink, Eye, Info, Loader2, Pencil, Plus, Tags, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Field, FormSection } from '@/components/common/FormBits'
import { EmptyState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { isApiError } from '@/lib/api/errors'
import { cn } from '@/lib/utils'
import { useSectorMutations } from '../../catalogApi'
import type { ProjectSummary, Sector } from '../../types'
import { toastError } from '../../ui'
import { ProjectIcon } from '../ProjectBits'

type Props = { sectors: Sector[]; projects: ProjectSummary[]; canWrite: boolean }

export function SectorList({ sectors, projects, canWrite }: Props) {
  const { t } = useTranslation()
  const { remove } = useSectorMutations()
  const [form, setForm] = useState<{ open: boolean; sector: Sector | null }>({ open: false, sector: null })
  const [viewing, setViewing] = useState<Sector | null>(null)
  const projectById = new Map(projects.map((p) => [p.id, p]))

  const onDelete = async (s: Sector) => {
    const ok = await confirmModal({
      title: t('projects.catalog.sectors.deleteTitle'),
      description: t('projects.catalog.sectors.deleteBody', { name: s.name }),
      confirmLabel: t('common.delete'),
      destructive: true,
    })
    if (!ok) return
    try {
      await remove.mutateAsync(s.id)
      toast.success(t('projects.catalog.sectors.deleted'))
    } catch (e) {
      const names = isApiError(e) ? (e.details as { projects?: string[] } | undefined)?.projects : undefined
      if (isApiError(e) && names) toast.error(t(`errors.${e.code}`), { description: names.join(', ') })
      else toastError(e)
    }
  }

  return (
    <div className="space-y-3">
      <div className="border-primary/15 bg-primary/[0.04] flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center">
        <Info className="text-primary size-5 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-semibold">{t('projects.catalog.sectors.whatTitle')}</p>
          <p className="text-muted-foreground">{t('projects.catalog.sectors.hint')}</p>
        </div>
        {canWrite ? (
          <Button onClick={() => setForm({ open: true, sector: null })}>
            <Plus />
            {t('projects.catalog.sectors.new')}
          </Button>
        ) : null}
      </div>

      <div className="bg-card shadow-card overflow-hidden rounded-xl border">
        {sectors.length === 0 ? (
          <EmptyState icon={Tags} title={t('projects.catalog.sectors.empty')} />
        ) : (
          <Table>
            <THead>
              <TR className="hover:bg-transparent">
                <TH>{t('projects.catalog.sectors.sector')}</TH>
                <TH>{t('projects.catalog.sectors.products')}</TH>
                <TH className="w-px text-right whitespace-nowrap">{t('projects.catalog.actions')}</TH>
              </TR>
            </THead>
            <TBody>
              {sectors.map((s) => {
                return (
                  <TR key={s.id}>
                    <TD>
                      <p className="font-medium">{s.name}</p>
                      <p className="text-muted-foreground text-xs">{s.description}</p>
                    </TD>
                    <TD>
                      {s.projectIds.length ? (
                        <div className="flex flex-wrap gap-1.5">
                          {s.projectIds.map((id) => {
                            const p = projectById.get(id)
                            return p ? (
                              <span
                                key={id}
                                className="bg-card inline-flex items-center gap-1.5 rounded-full border py-0.5 pr-2.5 pl-0.5 text-xs font-medium"
                              >
                                <ProjectIcon icon={p.icon} color={p.color} className="size-5 rounded-md shadow-none" />
                                {p.name}
                              </span>
                            ) : null
                          })}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">{t('projects.catalog.sectors.noProduct')}</span>
                      )}
                    </TD>
                    <TD>
                      <div className="flex justify-end gap-1">
                        <Tooltip content={t('projects.catalog.sectors.detail')} side="top">
                          <Button
                            size="icon"
                            variant="outline"
                            className="size-8"
                            aria-label={t('projects.catalog.sectors.detailFor', { name: s.name })}
                            onClick={() => setViewing(s)}
                          >
                            <Eye />
                          </Button>
                        </Tooltip>
                        {canWrite ? (
                          <>
                            <Tooltip content={t('common.edit')} side="top">
                              <Button
                                size="icon"
                                variant="outline"
                                className="size-8"
                                aria-label={t('projects.catalog.sectors.editFor', { name: s.name })}
                                onClick={() => setForm({ open: true, sector: s })}
                              >
                                <Pencil />
                              </Button>
                            </Tooltip>
                            <Tooltip content={t('common.delete')} side="top">
                              <Button
                                size="icon"
                                variant="outline"
                                className="text-destructive hover:text-destructive size-8"
                                aria-label={t('projects.catalog.sectors.deleteFor', { name: s.name })}
                                disabled={remove.isPending}
                                onClick={() => void onDelete(s)}
                              >
                                <Trash2 />
                              </Button>
                            </Tooltip>
                          </>
                        ) : null}
                      </div>
                    </TD>
                  </TR>
                )
              })}
            </TBody>
          </Table>
        )}
      </div>

      {form.open ? (
        <SectorForm sector={form.sector} projects={projects} onDone={() => setForm({ open: false, sector: null })} />
      ) : null}
      {viewing ? (
        <SectorDetail
          sector={viewing}
          projects={projects}
          canWrite={canWrite}
          onClose={() => setViewing(null)}
          onEdit={() => {
            setForm({ open: true, sector: viewing })
            setViewing(null)
          }}
        />
      ) : null}
    </div>
  )
}

function SectorDetail({
  sector,
  projects,
  canWrite,
  onClose,
  onEdit,
}: {
  sector: Sector
  projects: ProjectSummary[]
  canWrite: boolean
  onClose: () => void
  onEdit: () => void
}) {
  const { t } = useTranslation()
  const products = projects.filter((p) => sector.projectIds.includes(p.id))
  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onClose()}
      icon={<Eye />}
      title={sector.name}
      description={sector.description || undefined}
      footer={
        canWrite ? (
          <Button onClick={onEdit}>
            <Pencil />
            {t('common.edit')}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">{t('projects.catalog.sectors.products')}</h3>
          {products.length ? (
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {products.map((p) => (
                <li key={p.id} className="flex items-center gap-2.5 rounded-lg border p-2.5">
                  <ProjectIcon icon={p.icon} color={p.color} className="size-7 rounded-md shadow-none" />
                  <span className="min-w-0 flex-1 font-medium">{p.name}</span>
                  <Tooltip content={t('projects.catalog.packages.openProject')} side="top">
                    <Button size="icon" variant="outline" className="size-7" asChild>
                      <Link
                        to={`/projects/${p.id}`}
                        aria-label={t('projects.catalog.sectors.openProjectFor', { name: p.name })}
                      >
                        <ExternalLink className="size-3.5" />
                      </Link>
                    </Button>
                  </Tooltip>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground text-sm">{t('projects.catalog.sectors.noProduct')}</p>
          )}
        </section>
      </div>
    </Modal>
  )
}

function SectorForm({
  sector,
  projects,
  onDone,
}: {
  sector: Sector | null
  projects: ProjectSummary[]
  onDone: () => void
}) {
  const { t } = useTranslation()
  const { save } = useSectorMutations()
  const [name, setName] = useState(sector?.name ?? '')
  const [description, setDescription] = useState(sector?.description ?? '')
  const [projectIds, setProjectIds] = useState<string[]>(sector?.projectIds ?? [])
  const [error, setError] = useState('')
  const toggle = (id: string) => setProjectIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (name.trim().length < 2) {
      setError(t('projects.catalog.sectors.nameRequired'))
      return
    }
    try {
      await save.mutateAsync({ id: sector?.id, input: { name, description, projectIds } })
      toast.success(t('common.saved'))
      onDone()
    } catch (err) {
      if (isApiError(err) && err.code === 'SECTOR_NAME_TAKEN') setError(t('errors.SECTOR_NAME_TAKEN'))
      else toastError(err)
    }
  }

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<Tags />}
      title={sector ? t('projects.catalog.sectors.editTitle') : t('projects.catalog.sectors.new')}
      description={sector ? t('projects.catalog.sectors.renameHint') : undefined}
      onSubmit={(e) => void submit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <FormSection>
        <Field id="sc-name" label={t('projects.catalog.sectors.name')} error={error || undefined} full>
          <Input
            id="sc-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={!!error}
            maxLength={60}
          />
        </Field>
        <Field id="sc-desc" label={t('projects.catalog.sectors.description')} full>
          <Input id="sc-desc" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={160} />
        </Field>
        <fieldset className="sm:col-span-2">
          <legend className="mb-2 text-sm font-medium">{t('projects.catalog.sectors.products')}</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {projects.map((p) => {
              const on = projectIds.includes(p.id)
              return (
                <label
                  key={p.id}
                  className={cn(
                    'flex cursor-pointer items-center gap-2.5 rounded-lg border p-2.5 text-sm transition-colors',
                    on ? 'border-primary/40 bg-primary/[0.05]' : 'hover:bg-muted/50',
                  )}
                >
                  <input
                    type="checkbox"
                    className="size-4 accent-[var(--color-primary)]"
                    checked={on}
                    onChange={() => toggle(p.id)}
                  />
                  <ProjectIcon icon={p.icon} color={p.color} className="size-6 rounded-md shadow-none" />
                  <span className="font-medium">{p.name}</span>
                </label>
              )
            })}
          </div>
        </fieldset>
      </FormSection>
    </Modal>
  )
}
